import React, { createContext, useContext, useState } from 'react';
import type { Tenant } from '@/types/models';

export interface RecentScan {
  id: string;
  barcode: string;
  entityType: 'asset' | 'container' | 'work_order' | 'unknown';
  label?: string;
  timestamp: string;
  success: boolean;
}

interface AppContextType {
  sidebarCollapsed: boolean;
  setSidebarCollapsed: (v: boolean) => void;
  currentLocationId: string;
  setCurrentLocationId: (v: string) => void;
  scanModalOpen: boolean;
  setScanModalOpen: (v: boolean) => void;
  recentScans: RecentScan[];
  addRecentScan: (scan: RecentScan) => void;
  // Super admin tenant impersonation
  impersonatedTenantId: string | null;
  setImpersonatedTenantId: (id: string | null) => void;
}

const AppContext = createContext<AppContextType>({
  sidebarCollapsed: false,
  setSidebarCollapsed: () => {},
  currentLocationId: 'phoenix',
  setCurrentLocationId: () => {},
  scanModalOpen: false,
  setScanModalOpen: () => {},
  recentScans: [],
  addRecentScan: () => {},
  impersonatedTenantId: null,
  setImpersonatedTenantId: () => {},
});

export const useApp = () => useContext(AppContext);

export const AppProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [currentLocationId, setCurrentLocationId] = useState('phoenix');
  const [scanModalOpen, setScanModalOpen] = useState(false);
  const [recentScans, setRecentScans] = useState<RecentScan[]>([]);
  const [impersonatedTenantId, setImpersonatedTenantId] = useState<string | null>(null);

  const addRecentScan = (scan: RecentScan) => {
    setRecentScans((prev) => [scan, ...prev].slice(0, 20));
  };

  return (
    <AppContext.Provider
      value={{
        sidebarCollapsed, setSidebarCollapsed,
        currentLocationId, setCurrentLocationId,
        scanModalOpen, setScanModalOpen,
        recentScans, addRecentScan,
        impersonatedTenantId, setImpersonatedTenantId,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};
